<template>
    <div
        class="absolute top-full left-0 sm:left-auto sm:right-0 mt-1 z-30 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-md dark:shadow-slate-900/50 w-[calc(100vw-2rem)] sm:w-[640px] max-w-[640px]"
        @click.stop
        @mousedown.stop
        @touchstart.stop
    >
        <div class="flex flex-col sm:flex-row">
            <ul
                class="flex flex-row sm:flex-col gap-1 sm:w-36 p-3 border-b sm:border-b-0 sm:border-r border-slate-100 dark:border-slate-700 overflow-x-auto"
            >
                <li v-for="preset in PRESETS" :key="preset.value" class="shrink-0 sm:shrink">
                    <button
                        type="button"
                        class="w-full whitespace-nowrap px-3 py-2 text-left text-sm rounded font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                        :class="
                            draftPreset === preset.value
                                ? 'bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                        "
                        @click="selectPreset(preset.value)"
                    >
                        {{ preset.label }}
                    </button>
                </li>
            </ul>

            <div class="flex-1 p-4 flex flex-col gap-4">
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="flex-1 px-3 py-2 border rounded text-sm text-slate-800 dark:text-slate-100 text-left transition-colors"
                        :class="
                            focusedInput === 'start'
                                ? 'border-slate-800 ring-1 ring-slate-800 dark:border-slate-300 dark:ring-slate-300'
                                : 'border-slate-200 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500'
                        "
                        @click="focusedInput = 'start'"
                    >
                        {{ formatYM(draftStart) }}
                    </button>
                    <span class="text-slate-400">–</span>
                    <button
                        type="button"
                        class="flex-1 px-3 py-2 border rounded text-sm text-slate-800 dark:text-slate-100 text-left transition-colors"
                        :class="
                            focusedInput === 'end'
                                ? 'border-slate-800 ring-1 ring-slate-800 dark:border-slate-300 dark:ring-slate-300'
                                : 'border-slate-200 hover:border-slate-400 dark:border-slate-700 dark:hover:border-slate-500'
                        "
                        @click="focusedInput = 'end'"
                    >
                        {{ formatYM(draftEnd) }}
                    </button>
                </div>

                <!-- Desktop: 2-year side-by-side with prev/next nav -->
                <div class="hidden sm:grid grid-cols-2 gap-x-8">
                    <div class="flex items-center justify-between gap-2 mb-3">
                        <button
                            type="button"
                            class="p-1 rounded transition-colors text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                            aria-label="Previous year"
                            @click="shiftYears(-1)"
                        >
                            <Icon
                                icon="chevron-left-small"
                                class="w-4 h-4 text-slate-800 dark:text-slate-100"
                            />
                        </button>
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium"
                        >
                            {{ visibleYears[0] }}
                        </span>
                        <span class="w-6"></span>
                    </div>
                    <div class="flex items-center justify-between gap-2 mb-3">
                        <span class="w-6"></span>
                        <span
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium"
                        >
                            {{ visibleYears[1] }}
                        </span>
                        <button
                            type="button"
                            class="p-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                            :class="canShiftRight
                                ? 'text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600'
                                : 'cursor-not-allowed'"
                            aria-label="Next year"
                            :disabled="!canShiftRight"
                            @click="shiftYears(1)"
                        >
                            <Icon
                                icon="chevron-right-small"
                                :class="['w-4 h-4', canShiftRight ? 'text-slate-800 dark:text-slate-100' : 'text-slate-300 dark:text-slate-600']"
                            />
                        </button>
                    </div>

                    <div
                        v-for="(year, colIdx) in visibleYears"
                        :key="`year-${year}-${colIdx}`"
                        class="grid grid-cols-3 gap-y-1"
                    >
                        <button
                            v-for="(month, mIdx) in MONTHS"
                            :key="`m-${year}-${mIdx}`"
                            type="button"
                            :class="getMonthClass(year, mIdx)"
                            @click="clickMonth(year, mIdx)"
                        >
                            {{ month }}
                        </button>
                    </div>
                </div>

                <!-- Mobile: single-column scrollable list of years (Figma: Calendar.Scroll) -->
                <div
                    ref="mobileScrollEl"
                    class="sm:hidden overflow-y-auto max-h-[280px]"
                >
                    <div
                        v-for="(year, idx) in mobileYears"
                        :key="`m-year-${year}`"
                        :class="idx > 0 ? 'mt-4' : ''"
                    >
                        <div
                            class="text-center text-slate-800 dark:text-slate-100 text-sm font-medium mb-2"
                        >
                            {{ year }}
                        </div>
                        <div class="grid grid-cols-3 gap-y-1">
                            <button
                                v-for="(month, mIdx) in MONTHS"
                                :key="`mm-${year}-${mIdx}`"
                                type="button"
                                :class="getMonthClass(year, mIdx)"
                                @click="clickMonth(year, mIdx)"
                            >
                                {{ month }}
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    class="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700"
                >
                    <button
                        type="button"
                        class="btn-base btn-white"
                        @click="$emit('close')"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn-base btn-primary !bg-slate-800 hover:!bg-slate-700 !text-white !border-slate-800"
                        @click="apply"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import Icon from '@/components/ui/icon/icon.vue';

const emit = defineEmits(['close']);

const PRESETS = [
    { value: 'this-year', label: 'This year' },
    { value: 'previous-year', label: 'Previous year' },
    { value: 'this-month', label: 'This month' },
    { value: 'previous-month', label: 'Previous month' },
    { value: 'custom', label: 'Custom' },
];

const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

const store = useCardBenefitsStore();
const { insightsRangePreset, insightsDateRange } = storeToRefs(store);

const draftPreset = ref(insightsRangePreset.value);
const draftStart = ref({ year: 0, month: 0 });
const draftEnd = ref({ year: 0, month: 0 });
const visibleYears = ref([0, 0]);
const focusedInput = ref('start');

function ymFromDate(date) {
    const d = dayjs(date);
    return { year: d.year(), month: d.month() };
}

function loadFromStore() {
    const [start, end] = insightsDateRange.value;
    draftStart.value = ymFromDate(start);
    draftEnd.value = ymFromDate(end);
    draftPreset.value = insightsRangePreset.value;
    const currentYear = dayjs().year();
    visibleYears.value = [currentYear - 1, currentYear];
    focusedInput.value = 'start';
}

loadFromStore();

watch(insightsRangePreset, loadFromStore);

const mobileScrollEl = ref(null);

// Mobile shows a vertical scroll list of years; desktop shows 2-year columns
// with arrow nav. Range covers 5 prior years through current — enough for
// typical benefit history without unbounded scroll.
const mobileYears = computed(() => {
    const current = dayjs().year();
    return Array.from({ length: 6 }, (_, i) => current - 5 + i);
});

// Right arrow disables when the rightmost visible year is the current year
// (no future to navigate into). Left arrow stays enabled.
const canShiftRight = computed(() => visibleYears.value[1] < dayjs().year());

onMounted(async () => {
    await nextTick();
    if (mobileScrollEl.value) {
        mobileScrollEl.value.scrollTop = mobileScrollEl.value.scrollHeight;
    }
});

const today = computed(() => ymFromDate(new Date()));

function isFuture(year, month) {
    const t = today.value;
    return year > t.year || (year === t.year && month > t.month);
}

function ymKey(ym) {
    return ym.year * 12 + ym.month;
}

function getMonthClass(year, month) {
    const cell = ymKey({ year, month });
    const start = ymKey(draftStart.value);
    const end = ymKey(draftEnd.value);
    const future = isFuture(year, month);

    const isStart = cell === start;
    const isEnd = cell === end;
    const inRange = cell > start && cell < end;
    const isToday =
        year === today.value.year && month === today.value.month;

    const base =
        'h-9 flex items-center justify-center text-sm transition-colors rounded';
    // Today's-date indicator (Figma "Date picker states" spec): a thin
    // inset ring around the current month cell, layered on top of any
    // selection state without shifting layout.
    const todayRing = isToday
        ? ' ring-1 ring-inset ring-slate-300 dark:ring-slate-500'
        : '';

    if (isStart || isEnd) {
        return `${base} bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900 font-medium${todayRing}`;
    }
    if (inRange) {
        return `${base} bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100${todayRing}`;
    }
    if (future) {
        return `${base} text-slate-400 dark:text-slate-500 !cursor-not-allowed${todayRing}`;
    }
    return `${base} text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer${todayRing}`;
}

function selectPreset(value) {
    draftPreset.value = value;
    focusedInput.value = 'start';

    if (value === 'custom') {
        return;
    }

    const now = dayjs();
    if (value === 'this-year') {
        draftStart.value = { year: now.year(), month: 0 };
        draftEnd.value = { year: now.year(), month: now.month() };
    } else if (value === 'previous-year') {
        const ref = now.subtract(1, 'year');
        draftStart.value = { year: ref.year(), month: 0 };
        draftEnd.value = { year: ref.year(), month: 11 };
    } else if (value === 'this-month') {
        draftStart.value = { year: now.year(), month: now.month() };
        draftEnd.value = { year: now.year(), month: now.month() };
    } else if (value === 'previous-month') {
        const ref = now.subtract(1, 'month');
        draftStart.value = { year: ref.year(), month: ref.month() };
        draftEnd.value = { year: ref.year(), month: ref.month() };
    }
}

function clickMonth(year, month) {
    if (isFuture(year, month)) return;

    draftPreset.value = 'custom';
    const ym = { year, month };

    if (focusedInput.value === 'start') {
        draftStart.value = ym;
        if (ymKey(ym) > ymKey(draftEnd.value)) {
            draftEnd.value = ym;
        }
        focusedInput.value = 'end';
        return;
    }

    if (focusedInput.value === 'end') {
        if (ymKey(ym) < ymKey(draftStart.value)) {
            draftEnd.value = draftStart.value;
            draftStart.value = ym;
        } else {
            draftEnd.value = ym;
        }
        focusedInput.value = 'start';
    }
}

function shiftYears(direction) {
    visibleYears.value = [
        visibleYears.value[0] + direction,
        visibleYears.value[1] + direction,
    ];
}

function formatYM(ym) {
    if (!ym || ym.year === 0) return '';
    return `${MONTHS[ym.month]} ${ym.year}`;
}

function apply() {
    if (draftPreset.value === 'custom') {
        store.setInsightsCustomRange({
            startYear: draftStart.value.year,
            startMonth: draftStart.value.month,
            endYear: draftEnd.value.year,
            endMonth: draftEnd.value.month,
        });
    } else {
        store.setInsightsRangePreset(draftPreset.value);
    }
    emit('close');
}
</script>
